import React, {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {
  FiBarChart,
  FiBookOpen,
  FiCheckSquare,
  FiChevronDown,
  FiChevronRight,
  FiFile,
  FiLayers,
  FiMail,
  FiSearch,
  FiTarget,
  FiUserCheck,
  FiUsers,
} from 'react-icons/fi'
import {VmoAccordion, VmoAccordionContent, VmoAccordionTitle, VmoMenu, VmoMenuItem} from 'vmo-library'

import PermissionIcon from 'components/common/PermissionIcon'
import useUserPermissions from 'hooks/pure/useUserPermission'
import {isActiveNavlink} from 'utils/helper'
import {ANALYTICS_PATH} from 'constants/path'

const AnalyticsMenu = () => {
  const navigate = useNavigate()

  const [expand, setExpand] = useState([])

  const {advanceFeaturesAccess, analyticsAccess, conversationAccess, docsPermissions, reportsPermissions} =
    useUserPermissions()
  const allowStaffingPredictionFeature = conversationAccess?.staffingPrediction
  const allowTaskManagementFeature = advanceFeaturesAccess?.taskManagement
  const allowAssetManagementFeature = advanceFeaturesAccess?.assetManagement
  const allowSlaFeature = analyticsAccess?.sla
  const allowCustomerSatisfactionFeature = analyticsAccess?.customerSatisfaction

  const allowInboxReportsRole = reportsPermissions?.inbox_reports
  const allowTaskReportsRole = reportsPermissions?.task_reports
  const allowAssetReportsRole = reportsPermissions?.asset_reports
  const allowStatusPageReportsRole = reportsPermissions?.status_page_report
  const allowPerformanceReportsRole = reportsPermissions?.performance_reporting
  const allowDocsReportsRole = docsPermissions?.docs_reports

  const menus = [
    {
      key: 'overview',
      path: ANALYTICS_PATH.OVERVIEW,
      icon: <FiBookOpen />,
      title: 'Overview',
    },
    {
      key: 'conversations',
      title: 'Conversations',
      items: [
        {
          key: 'inbox',
          path: ANALYTICS_PATH.INBOX,
          icon: <FiMail />,
          title: 'Inbox',
          isRoleAccessDenied: !allowInboxReportsRole,
        },
        {
          key: 'tags',
          path: ANALYTICS_PATH.TAGS,
          icon: <FiMail />,
          title: 'Tags',
          isRoleAccessDenied: !allowInboxReportsRole,
        },
        {
          key: 'customer-satisfaction',
          path: ANALYTICS_PATH.CUSTOMER_SATISFACTION,
          icon: <FiUserCheck />,
          title: 'Customer Satisfaction',
          isFeatureAccessDenied: !allowCustomerSatisfactionFeature,
          isRoleAccessDenied: !allowInboxReportsRole,
        },
      ],
    },
    {
      key: 'performance',
      title: 'Performance',
      items: [
        {
          key: 'metrics',
          path: ANALYTICS_PATH.METRICS,
          icon: <FiBarChart />,
          title: 'Metrics',
          isRoleAccessDenied: !allowPerformanceReportsRole,
        },
        {
          key: 'users',
          path: ANALYTICS_PATH.USERS,
          icon: <FiUsers />,
          title: 'Users',
          isRoleAccessDenied: !allowPerformanceReportsRole,
        },
        {
          key: 'staffing-prediction',
          path: ANALYTICS_PATH.STAFFING_PREDICTION,
          icon: <FiCheckSquare />,
          title: 'Staffing Prediction',
          isFeatureAccessDenied: !allowStaffingPredictionFeature,
          isRoleAccessDenied: !allowPerformanceReportsRole,
        },
        {
          key: 'agent-utilization',
          path: ANALYTICS_PATH.AGENT_UTILIZATION,
          icon: <FiCheckSquare />,
          title: 'Agent Utilization',
          isRoleAccessDenied: !allowPerformanceReportsRole,
        },
        {
          key: 'sla',
          path: ANALYTICS_PATH.SLA,
          icon: <FiTarget />,
          title: 'SLA',
          isFeatureAccessDenied: !allowSlaFeature,
          isRoleAccessDenied: !allowPerformanceReportsRole,
        },
      ],
    },
    {
      key: 'knowledgebase',
      title: 'Knowledge Base',
      items: [
        {
          key: 'articles',
          path: ANALYTICS_PATH.ARTICLES,
          icon: <FiFile />,
          title: 'Articles',
          isRoleAccessDenied: !allowDocsReportsRole,
        },
        {
          key: 'searches',
          path: ANALYTICS_PATH.SEARCHES,
          icon: <FiSearch />,
          title: 'Searches',
          isRoleAccessDenied: !allowDocsReportsRole,
        },
      ],
    },
    {
      key: 'tasks-analytics',
      path: ANALYTICS_PATH.TASKS_ANALYTICS,
      icon: <FiBookOpen />,
      title: 'Tasks Analytics',
      isFeatureAccessDenied: !allowTaskManagementFeature,
      isRoleAccessDenied: !allowTaskReportsRole,
    },
    {
      key: 'assets-analytics',
      path: ANALYTICS_PATH.ASSETS_ANALYTICS,
      icon: <FiLayers />,
      title: 'Assets Analytics',
      isFeatureAccessDenied: !allowAssetManagementFeature,
      isRoleAccessDenied: !allowAssetReportsRole,
    },
    {
      key: 'status-page',
      path: ANALYTICS_PATH.STATUS_PAGE,
      icon: <FiFile />,
      title: 'Status Page',
      isRoleAccessDenied: !allowStatusPageReportsRole,
    },
  ]

  const handleMenuExpand = key => {
    const menus = [...expand]
    if (menus.includes(key)) {
      menus.splice(menus.indexOf(key), 1)
      setExpand(menus)
    } else {
      setExpand([...menus, key])
    }
  }
  const isActive = key => !expand.includes(key)

  const renderMenuItem = route => {
    return (
      <VmoMenuItem
        key={route.key}
        active={isActiveNavlink(route.path, route.exact)}
        onClick={() => navigate(route.path)}
      >
        {route?.icon}
        {route?.title}
        <span>
          <PermissionIcon
            isFeatureAccessDenied={route?.isFeatureAccessDenied}
            isRoleAccessDenied={route?.isRoleAccessDenied}
          />
        </span>
      </VmoMenuItem>
    )
  }

  return (
    <VmoMenu secondary vertical className="set-list">
      {menus.map(menu =>
        menu.items?.length ? (
          <VmoAccordion key={menu.key}>
            <VmoAccordionTitle onClick={() => handleMenuExpand(menu.key)}>
              {menu.title}
              {isActive(menu.key) ? <FiChevronDown /> : <FiChevronRight />}
            </VmoAccordionTitle>
            <VmoAccordionContent active={isActive(menu.key)}>
              {menu.items.map(route => renderMenuItem(route))}
            </VmoAccordionContent>
          </VmoAccordion>
        ) : (
          renderMenuItem(menu)
        )
      )}
    </VmoMenu>
  )
}

export default AnalyticsMenu
